import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import DashboardPage from './pages/DashboardPage';
import SubmissionsPage from './pages/SubmissionsPage';
import SubmissionDetailPage from './pages/SubmissionDetailPage';
import AuthorsPage from './pages/AuthorsPage';
import LatexPage from './pages/LatexPage';
import EditorialBoardPage from './pages/EditorialBoardPage';
import PromotionsPage from './pages/PromotionsPage';
import DoiDashboardPage from './pages/DoiDashboardPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';

import SuperUsersPage from './pages/SuperUsersPage';
import {
  getCurrentAdminProfile,
  getJournals,
  getActiveJournalSlug,
  setActiveJournalSlug,
} from './api/client';

// ── Auth Context ──
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
const TOKEN_KEY = 'uzjs_token';
const ADMIN_PROFILE_STORAGE_KEY = 'uzjs_admin_profile';

const readStoredAdminProfile = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(ADMIN_PROFILE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const persistAdminProfile = (profile) => {
  if (typeof window === 'undefined') return;
  if (profile) {
    localStorage.setItem(ADMIN_PROFILE_STORAGE_KEY, JSON.stringify(profile));
    return;
  }
  localStorage.removeItem(ADMIN_PROFILE_STORAGE_KEY);
};

function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    // Unified token: prefer uzjs_token, migrate from legacy 'token' key
    const unified = localStorage.getItem(TOKEN_KEY);
    if (unified) return unified;
    const legacy = localStorage.getItem('token');
    if (legacy) {
      localStorage.setItem(TOKEN_KEY, legacy);
      localStorage.removeItem('token');
      return legacy;
    }
    return null;
  });
  const [admin, setAdmin] = useState(() => readStoredAdminProfile());
  const [journals, setJournals] = useState([]);
  const [selectedJournalSlug, setSelectedJournalSlugState] = useState(() => getActiveJournalSlug());
  const [bootstrapping, setBootstrapping] = useState(() => !!localStorage.getItem(TOKEN_KEY));

  const applyJournalSelection = useCallback((slug, availableJournals) => {
    const scope = Array.isArray(availableJournals) ? availableJournals : journals;
    const allowedSlugs = new Set(scope.map((journal) => journal.slug));
    let nextSlug = (slug || '').trim();
    if (scope.length > 0 && (!nextSlug || !allowedSlugs.has(nextSlug))) {
      nextSlug = scope[0].slug;
    }
    setSelectedJournalSlugState(nextSlug);
    setActiveJournalSlug(nextSlug);
    return nextSlug;
  }, []);

  const loginUser = (newToken, adminSession = null) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.removeItem('token'); // cleanup legacy
    setToken(newToken);
    if (adminSession) {
      setAdmin(adminSession);
      persistAdminProfile(adminSession);
    }
    setBootstrapping(true);
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('token'); // cleanup legacy
    persistAdminProfile(null);
    setActiveJournalSlug('');
    setToken(null);
    setAdmin(null);
    setJournals([]);
    setSelectedJournalSlugState('');
    setBootstrapping(false);
  }, []);

  useEffect(() => {
    persistAdminProfile(admin);
  }, [admin]);

  useEffect(() => {
    if (!token) return;
    let active = true;

    const hydrateAdminSession = async () => {
      setBootstrapping(true);
      try {
        const [profileRes, journalsRes] = await Promise.all([
          getCurrentAdminProfile(),
          getJournals({ active_only: true }),
        ]);
        if (!active) return;

        const nextAdmin = profileRes.data;
        const accessibleJournals = Array.isArray(journalsRes.data) ? journalsRes.data : [];
        setAdmin(nextAdmin);
        setJournals(accessibleJournals);
        applyJournalSelection(getActiveJournalSlug(), accessibleJournals);
      } catch (error) {
        if (!active) return;
        logout();
      } finally {
        if (active) {
          setBootstrapping(false);
        }
      }
    };

    hydrateAdminSession();
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [logout]);

  const currentJournal = useMemo(() => (
    journals.find((journal) => journal.slug === selectedJournalSlug) || journals[0] || null
  ), [journals, selectedJournalSlug]);

  const setSelectedJournalSlug = useCallback((slug) => {
    applyJournalSelection(slug);
  }, [applyJournalSelection]);

  const contextValue = useMemo(() => ({
    token,
    admin,
    journals,
    currentJournal,
    selectedJournalSlug,
    isAuthenticated: !!token,
    isSuperAdmin: admin?.role === 'superadmin' || !!admin?.is_super_admin,
    authReady: !bootstrapping,
    loginUser,
    logout,
    setSelectedJournalSlug,
  }), [
    token,
    admin,
    journals,
    currentJournal,
    selectedJournalSlug,
    bootstrapping,
    logout,
    setSelectedJournalSlug,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, authReady } = useAuth();
  if (!isAuthenticated) {
    // Redirect to unified public login page
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.hash);
    window.location.href = `/pages/login.html?return=${returnUrl}`;
    return null;
  }
  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-700 border-t-transparent" />
      </div>
    );
  }
  return children;
}

function KeyboardShortcuts() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e) => {
      // Skip if user is typing in an input
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.altKey) {
        switch (e.key) {
          case 'h': e.preventDefault(); navigate('/'); break;
          case 's': e.preventDefault(); navigate('/submissions'); break;
          case 'a': e.preventDefault(); navigate('/authors'); break;
          case 'l': e.preventDefault(); navigate('/latex'); break;
          case 'e': e.preventDefault(); navigate('/editorial-board'); break;
          default: break;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <HashRouter>
        <Toaster position="top-right" />
        <KeyboardShortcuts />
        <Routes>
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path="/submissions" element={<SubmissionsPage />} />
                      <Route path="/submissions/:id" element={<SubmissionDetailPage />} />
                      <Route path="/authors" element={<AuthorsPage />} />
                      <Route path="/latex" element={<LatexPage />} />
                      <Route path="/editorial-board" element={<EditorialBoardPage />} />
                      <Route path="/promotions" element={<PromotionsPage />} />
                      <Route path="/doi-dashboard" element={<DoiDashboardPage />} />
                      <Route path="/email-templates" element={<EmailTemplatesPage />} />
                      <Route path="/super/users" element={<SuperUsersPage />} />
                    </Routes>
                  </Layout>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
        </Routes>
      </HashRouter>
    </AuthProvider>
  );
}

export default App;
